import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { Button, SkeletonLoader } from "app/components";
import {
  MapPin,
  Calendar,
  Ticket,
  Megaphone,
  CopySimple,
  MapTrifold,
  VideoCamera,
  CircleNotch,
} from "@phosphor-icons/react";
import CopyToClipboard from "react-copy-to-clipboard";
import { getPrimaryEventById } from "../api";
import { formatDate } from "../../../utils/formatDate";
import NotFound from "../../not-found/notFound";

import "./style";
import { EventMap } from "../../event-create/components/step-1";
import { onShareWhatsapp } from "../../../utils/commonUtils";
import ShareEvent from "./shareEvent";
import { isPast } from 'date-fns';
import { TIME_INTERVAL_CONFIG } from "../../../constants/timeIntervalConfig";

const EventPage = (props) => {
  const {} = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedEventLink, setCopiedEventLink] = useState(false);
  const [primaryEventData, setPrimaryEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState("offline");
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [eventClosed, setClosed] = useState(false);
  if (!/^[a-f\d]{24}$/i.test(id)) {
    return <NotFound />;
  }

  useEffect(() => {
    window.scrollTo(0, 0);

    setLoading(true);
    getPrimaryEventById(id, (data) => {
      if (data.payload) {
          setPrimaryEventData({ ...data?.payload, smp_id: data?.payload?.id, organiser_dtls: data?.organiser, id: data?._id || id });
          dispatch({
            type: "PRIMARY_EVENT_DATA:SET",
            data: { ...data?.payload, organiser_dtls: data?.organiser, smp_id: data?.payload?.id, id: data?._id || id },
          });
          setLoading(false);
          function isDatePassed(dateString) {
            const inputDate = new Date(dateString.replace(" ", "T")); 
            const currentDate = new Date();
            const closeTime = new Date(inputDate.getTime() - TIME_INTERVAL_CONFIG.TICKET_CLOSE);
            return currentDate > closeTime
          }
          // setClosed(isDatePassed(data?.payload.start_date) || !data?.payload.is_active);
          setClosed(isPast(new Date(data?.payload?.end_date)));
        } else {
          setLoading(false);
          setPrimaryEventData(null);
        }
      });
  }, []);

  const handleBuyTickets = () => {
    navigate("/event/add-category", { state: { id: id } });
  };

  const handleCopyTicket = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopiedEventLink(true);
      setTimeout(() => setCopiedEventLink(false), 2000);
    }
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };
  
  const EventDescription = ({ description }) => {
    return <div dangerouslySetInnerHTML={{ __html: description }} />;
  };

  const text = primaryEventData?.description || "";

  // Split the text into an array of words
  const words = text.split(" ");
  const wordLimit = 50;

  // Check if the description exceeds the word limit
  const isLongText = words.length > wordLimit;

  // Truncate the text if it's longer than the word limit
  const shortText = isLongText ? words.slice(0, wordLimit).join(" ") : text;

  if (loading) {
    return (
      <div className="mt-[70px] h-screen overflow-y-scroll md:h-screen md:overflow-auto md:items-start
        md:mt-[100px] p-5 relative md:flex md:justify-center md:gap-[25px] md:px-[80px] md:py-7 md:bg-grey-5">
        <SkeletonLoader type="card" count={1} className="w-[811px]" />
        <SkeletonLoader type="card" count={1} className="w-[420px]" />
      </div>
    );
  }

  const getShareText = () => encodeURI(`Hey! Checkout this event listed in Take My Tickets! ${window.location.href}`);

  const isTicketsSoldOut = primaryEventData?.smp_detail_entity_set?.every((item) => item?.available_quantity === 0);
  const isCancelled = primaryEventData?.status?.toLowerCase() === "cancelled";

  if (!loading && !primaryEventData?.name) {
    return <NotFound />;
  }

  // Prepare Open Graph meta tags data
  const ogTitle = primaryEventData?.name;
  const ogDescription = primaryEventData?.description
    ? primaryEventData.description.length > 120
      ? primaryEventData.description.substring(0, 117) + "..."
      : primaryEventData.description
    : `Join us for ${primaryEventData?.name} on ${formatDate(primaryEventData?.start_date)} in ${primaryEventData?.event_address?.city || "Online"}.`;
  const ogImage = primaryEventData?.image 
  const ogUrl = window.location.href;

  return (
    <>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />

        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={"https://takemytickets.co.in/"} />
        <meta property="og:type" content="event" />
        <meta property="og:site_name" content="Take My Tickets" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <div
        className="mt-[70px] h-screen overflow-y-scroll md:h-full md:overflow-auto md:items-start
        md:mt-[100px] p-5 relative md:flex md:justify-center md:gap-[25px] md:px-[80px] md:py-7 md:bg-grey-5"
      >
        <div className="event-bg"></div>

        <div className="md:w-[811px] md:bg-white md:p-3 md:rounded-[20px] md:sha-2">
          <img
            src={primaryEventData?.image}
            className="h-[204px] rounded-2xl object-cover mt-4 md:h-[420px] md:w-full md:mt-0"
          />

          <div className="flex flex-col gap-9 pt-5 mb-[156px] md:mb-auto md:px-5 md:py-10">
            <div className="flex flex-col gap-4 md:hidden">
              <div className="flex flex-col gap-3">
                <h1 className="heading-24 break-all">{primaryEventData?.name}</h1>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-3 items-start">
                    <div className="p-[7px] sha-1">
                      {primaryEventData?.type === "online" ? (
                        <VideoCamera size={20.571} />
                      ) : (
                        <MapTrifold size={20.571} />
                      )}
                    </div>
                    {primaryEventData?.type === "online" ? (
                      <h1 className="label-14 self-center">Online event</h1>
                    ) : (
                      <div className="flex flex-col gap-1 self-center">
                        <h1 className="label-14">
                          {" "}
                          {primaryEventData?.event_address?.name},{" "}
                          {primaryEventData?.geo_location}{" "}
                        </h1>
                        <p className="text-14 text-grey-70">
                          {`${primaryEventData?.event_address?.address_line_1}${primaryEventData?.event_address?.address_line_2 ? `, ${primaryEventData?.event_address?.address_line_2}` : ''}, ${primaryEventData?.event_address?.city}`}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 items-center">
                    <div className="p-[7px] sha-1">
                      <Calendar size={20.571} />
                    </div>
                    <p className="text-14">
                      {formatDate(primaryEventData?.start_date)}
                    </p>
                  </div>
                  {/* <div className="flex gap-3 items-center">
                    <div className="p-[7px] sha-1">
                      <Ticket size={20.571} />
                    </div>
                    <p className="text-14">
                      Tickets starting at
                      <span className="ml-[6px] text-[#009D64] font-medium">
                        ₹ 1,000 onwards
                      </span>
                    </p>
                  </div> */}
                </div>
              </div>
              {!isTicketsSoldOut && !isCancelled && (!eventClosed && (<div className="flex gap-4 items-center py-[4px] px-[8px] bg-light rounded-[4px] w-max">
                <Megaphone size={20} className="text-primary -scale-x-100" />
                <p className="label-14 text-primary">Ticket sale ends soon</p>
              </div>))}
            </div>

            <div className="flex flex-col gap-4">
              <h1 className="heading-16 md:text-2xl">About the event</h1>
              <p className="text-grey-70">
                {isLongText && !isExpanded ? (
                  <EventDescription description={shortText} />
                ) : (
                  <EventDescription description={text} />
                )}
                {isLongText && (
                  <span
                    onClick={handleToggle}
                    className="heading-16 text-primary cursor-pointer"
                  >
                    {isExpanded ? " Read less" : " Read more"}
                  </span>
                )}
              </p>
            </div>

            {primaryEventData?.note && 
              <div className="flex flex-col gap-4">
                <h1 className="heading-16 md:text-2xl">Terms and Conditions</h1>
                <p className="text-grey-70">
                  <EventDescription description={primaryEventData?.note} />
                </p>
              </div>
            }

            <ShareEvent link={window.location.href} />

            <div className="flex flex-col gap-[18px]">
              <h1 className="heading-16 md:text-2xl">Venue</h1>
              <div className="flex gap-4 items-center">
                <div className="p-[7px] sha-1">
                  {primaryEventData?.type === "online" ? (
                    <VideoCamera size={20.571} />
                  ) : (
                    <MapTrifold size={20.571} />
                  )}
                </div>
                {primaryEventData?.type === "online" ? (
                  <h1 className="label-14">Online event</h1>  
                ) : (
                  <div className="flex flex-col gap-1">
                    <h1 className="label-14">
                      {" "}
                      {primaryEventData?.event_address?.name},{" "}
                      {primaryEventData?.geo_location}{" "}
                    </h1>
                    <p className="text-14 text-grey-70">
                      {`${primaryEventData?.event_address?.address_line_1}${primaryEventData?.event_address?.address_line_2 ? `, ${primaryEventData?.event_address?.address_line_2}` : ''}, ${primaryEventData?.event_address?.city}`}
                    </p>
                  </div>
                )}
              </div>
              {primaryEventData?.type === 'online' && <p className="text-16">
                Event link and details will be shared once you make the purchase.
              </p>}
          
              {(primaryEventData?.type === 'offline' && primaryEventData?.event_address?.location) && (
                <div className="h-[220px] rounded-xl flex justify-center items-center bg-grey-10 md:h-[300px]">
                  <EventMap formData={primaryEventData} showHeader={false} isMarkerDraggable={false}/>
                </div>
              )}
            </div>

            {/* {primaryEventData?.organiser_dtls?.company_name && (<div className="flex flex-col gap-4">
              <h1 className="heading-16 md:text-2xl">Organized by</h1>
              <div className="p-5 rounded-xl bg-grey-10 flex flex-col gap-4 items-center justify-center md:flex-row md:justify-start">
                <img
                  src={primaryEventData?.organiser_dtls?.logo ? `${process.env.appConfig.API_URL}/image/${primaryEventData.organiser_dtls.logo}` :"assets/images/badge.svg"}
                  className="w-[100px] h-[100px] rounded-full"
                />
                <div className="flex flex-col gap-2 items-center md:items-start">
                  <div className="flex gap-2 items-center md:justify-start">
                    <h1 className="heading-16">{primaryEventData?.organiser_dtls?.company_name}</h1>
                    <img src="assets/images/ticket-verified.svg" />
                  </div>
                  <p className="text-14 text-center md:text-start">
                    {primaryEventData?.organiser_dtls?.note}
                  </p>
                </div>
              </div>
            </div>)} */}
          </div>
        </div>
        <div
          className="fixed bottom-0 left-0 w-full mt-auto pt-5 pb-6 px-6 flex flex-col gap-3 
          items-center border-t-[#FFF2F2] border-t border-solid bg-[#FDFDFD] md:hidden z-50"
        >
          {eventClosed ? <Button className = "w-full mt-[32px]" disabled = {true}>Closed</Button> :
            <Button className="w-full mt-[32px]" onClick={handleBuyTickets} disabled={isTicketsSoldOut || isCancelled}>
              {isCancelled ? 'Event Cancelled' : isTicketsSoldOut ? 'Sold Out' : 'Buy tickets'}
            </Button>}
        </div>
        <div className="w-[420px] relative">
          <div className="hidden md:flex flex-col gap-4 p-8 w-[420px] bg-white rounded-[20px] sha-2 h-fit md:fixed md:">
            <div className="flex flex-col gap-3">
              <h1 className="heading-36 break-all">{primaryEventData?.name}</h1>
              <div className="flex flex-col gap-2">
                <div className="flex gap-3 items-start">
                  <div className="p-[7px] sha-1">
                    {primaryEventData?.type === "online" ? (
                      <VideoCamera size={20.571} />
                    ) : (
                      <MapTrifold size={20.571} />
                    )}
                  </div>
                  {primaryEventData?.type === "online" ? (
                    <h1 className="label-14 self-center">Online event</h1>
                  ) : (
                    <div className="flex flex-col gap-1 self-center">
                      <h1 className="label-14">
                        {" "}
                        {primaryEventData?.event_address?.name},{" "}
                        {primaryEventData?.geo_location}{" "}
                      </h1>
                      <p className="text-14 text-grey-70">
                        {`${primaryEventData?.event_address?.address_line_1}${primaryEventData?.event_address?.address_line_2 ? `, ${primaryEventData?.event_address?.address_line_2}` : ''}, ${primaryEventData?.event_address?.city}`}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 items-center">
                  <div className="p-[7px] sha-1">
                    <Calendar size={20.571} />
                  </div>
                  <p className="text-14">
                    {" "}
                    {formatDate(primaryEventData?.start_date)}
                  </p>
                </div>
                {/* <div className="flex gap-3 items-center">
                  <div className="p-[7px] sha-1">
                    <Ticket size={20.571} />
                  </div>
                  <p className="text-14">
                    Tickets starting at
                    <span className="ml-[6px] text-[#009D64] font-medium">
                      ₹ 1,000 onwards
                    </span>
                  </p>
                </div> */}
              </div>
            </div>
            {!isTicketsSoldOut && !isCancelled && (!eventClosed && (<div className="flex gap-4 items-center py-[4px] px-[8px] bg-light rounded-[4px] w-max">
              <Megaphone size={20} className="text-primary -scale-x-100" />
              <p className="label-14 text-primary">Ticket sale ends soon</p>
            </div>))}
            {eventClosed ? <Button className = "w-full mt-[32px]" disabled = {true}>Closed</Button> :
            <Button className="w-full mt-[32px]" onClick={handleBuyTickets} disabled={isTicketsSoldOut || isCancelled}>
              {isCancelled ? 'Event Cancelled' : isTicketsSoldOut ? 'Sold Out' : 'Buy tickets'}
            </Button>}
          </div>
        </div>
      </div>
    </>
  );
};

export default EventPage;
